import { create } from "zustand";
import type {
  AssetRecord,
  Collection,
  ColorLabel,
  JobRecord,
  RootRecord,
  SavedSearch
} from "@focalfolio/domain-model";

export type ViewMode = "grid" | "list";
export type InspectorTab = "metadata" | "adjust" | "file";

export interface Notice {
  id: string;
  level: "info" | "success" | "warning" | "error";
  message: string;
  at: number;
}

interface WorkspaceState {
  initialized: boolean;
  writer: boolean;
  compatibilityMode: boolean;
  roots: RootRecord[];
  activeRootId?: string;
  currentFolder: string;
  assets: AssetRecord[];
  totalAssets: number;
  collections: Collection[];
  savedSearches: SavedSearch[];
  jobs: JobRecord[];
  selectedIds: string[];
  primaryId?: string;
  compareIds: string[];
  previewId?: string;
  search: string;
  minimumRating: number;
  labelFilter: ColorLabel | "all";
  kindFilter: string;
  sort: "filename" | "captureDate" | "size" | "lastModified" | "rating";
  direction: "asc" | "desc";
  viewMode: ViewMode;
  inspectorTab: InspectorTab;
  scanning: boolean;
  scanProgress: { discovered: number; metadata: number; phase: string };
  busy: boolean;
  busyMessage: string;
  notices: Notice[];
  set: (change: Partial<WorkspaceState>) => void;
  select: (id: string, options?: { additive?: boolean; range?: boolean }) => void;
  clearSelection: () => void;
  notice: (level: Notice["level"], message: string) => void;
  dismissNotice: (id: string) => void;
}

export const useWorkspaceStore = create<WorkspaceState>((set, get) => ({
  initialized: false,
  writer: true,
  compatibilityMode: false,
  roots: [],
  currentFolder: "",
  assets: [],
  totalAssets: 0,
  collections: [],
  savedSearches: [],
  jobs: [],
  selectedIds: [],
  compareIds: [],
  search: "",
  minimumRating: 0,
  labelFilter: "all",
  kindFilter: "all",
  sort: "filename",
  direction: "asc",
  viewMode: "grid",
  inspectorTab: "metadata",
  scanning: false,
  scanProgress: { discovered: 0, metadata: 0, phase: "Idle" },
  busy: false,
  busyMessage: "",
  notices: [],
  set: (change) => set(change),
  select: (id, options = {}) => {
    const state = get();
    if (options.range && state.primaryId) {
      const start = state.assets.findIndex((asset) => asset.id === state.primaryId);
      const end = state.assets.findIndex((asset) => asset.id === id);
      if (start >= 0 && end >= 0) {
        const range = state.assets.slice(Math.min(start, end), Math.max(start, end) + 1).map((asset) => asset.id);
        set({ selectedIds: options.additive ? [...new Set([...state.selectedIds, ...range])] : range, primaryId: id });
        return;
      }
    }
    if (options.additive) {
      const selected = state.selectedIds.includes(id) ? state.selectedIds.filter((value) => value !== id) : [...state.selectedIds, id];
      set({ selectedIds: selected, primaryId: id });
      return;
    }
    set({ selectedIds: [id], primaryId: id });
  },
  clearSelection: () => set({ selectedIds: [], primaryId: undefined, compareIds: [] }),
  notice: (level, message) => {
    const notice: Notice = { id: crypto.randomUUID(), level, message, at: Date.now() };
    set({ notices: [...get().notices.slice(-4), notice] });
    if (level !== "error") setTimeout(() => get().dismissNotice(notice.id), 5000);
  },
  dismissNotice: (id) => set({ notices: get().notices.filter((notice) => notice.id !== id) })
}));
